/**
 * economy.ts — balance + transactions + savings + challenges + investments
 * (PR-15 split of api/client.ts).
 *
 * Everything money-shaped that isn't shop or chore reward goes here:
 * the wallet balance, the running transaction list, the compound-interest
 * savings sub-account, parent-issued cost/reward challenges, and the
 * paper-trading investment market.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Heaviest user: WalletPage (wallet/savings/investment tabs).
 *     HomePage uses listChallenges + resolveChallenge for the side rail.
 *   - Backend: backend/src/routes/savings.js, challenges.js, investments.js
 *     and the transactions slice of /api/v1/child/transactions.
 *
 * 关键约束：
 *   - listTransactions returns earned_total / spent_total that exclude
 *     internal savings transfers — UI must show those numbers, NOT a
 *     client-side sum of the transactions array (which would double-count
 *     deposit/withdraw movements).
 */
import type { Transaction } from '@xiaopeiban/shared';
import { call } from './_core';

export interface InvestmentAsset {
  id: number;
  code: string;
  display_name: string;
  description: string | null;
  emoji: string | null;
  risk_level: 'low' | 'medium' | 'high';
  baseline_price: number;
  current_price: number;
  volatility: number;
  unlock_level: number;
}

export interface InvestmentHolding {
  asset_id: number;
  quantity: number;
  avg_buy_price: number;
  total_invested: number;
  first_buy_at: number;
  last_buy_at: number;
}

export interface InvestmentMarket {
  assets: InvestmentAsset[];
  holdings: InvestmentHolding[];
  wallet_balance: number;
}

export interface ChildChallenge {
  id: number;
  title: string;
  description: string | null;
  icon: string | null;
  cost: number;
  reward_points: number;
  deadline: number | null;
  created_at: number;
}

export const economyApi = {
  // Money
  getBalance() {
    return call<{ balance: number }>('/api/v1/child/balance');
  },
  listTransactions(limit = 20) {
    return call<{
      transactions: Transaction[];
      /** Lifetime sum of all positive transactions (sen). Excludes
       *  savings_deposit/withdraw internal transfers. */
      earned_total: number;
      /** Lifetime sum of all negative transactions (sen, ≤ 0). */
      spent_total: number;
    }>(
      `/api/v1/child/transactions?limit=${limit}`,
    );
  },

  // Savings (daily compound)
  getSavings() {
    return call<{
      balance: number; total_interest_earned: number;
      interest_rate_pct_per_day: number; last_interest_at: number;
    }>('/api/v1/child/savings');
  },
  depositSavings(amount: number) {
    return call<{ ok: true; savings_balance: number; main_balance: number }>(
      '/api/v1/child/savings/deposit',
      { method: 'POST', body: JSON.stringify({ amount }) },
    );
  },
  withdrawSavings(amount: number) {
    return call<{ ok: true; savings_balance: number }>(
      '/api/v1/child/savings/withdraw',
      { method: 'POST', body: JSON.stringify({ amount }) },
    );
  },

  // Challenges (child view)
  listChallenges() {
    return call<{ challenges: ChildChallenge[] }>('/api/v1/child/challenges');
  },
  resolveChallenge(id: number) {
    return call<{ ok: true; net_change: number }>(
      `/api/v1/child/challenges/${id}/resolve`,
      { method: 'POST' },
    );
  },

  // Investments
  investmentMarket() {
    return call<InvestmentMarket>('/api/v1/child/investments/market');
  },
  investmentHistory(code: string) {
    return call<{
      asset: { code: string; display_name: string; baseline_price: number; current_price: number };
      history: { price: number; recorded_at: number }[];
    }>(`/api/v1/child/investments/asset/${code}/history`);
  },
  investmentBuy(asset_id: number, quantity: number) {
    return call<{
      ok: true;
      bought: { asset: string; quantity: number; price: number; total: number };
      balance_after: number;
    }>('/api/v1/child/investments/buy', {
      method: 'POST', body: JSON.stringify({ asset_id, quantity }),
    });
  },
  investmentSell(asset_id: number, quantity: number) {
    return call<{
      ok: true;
      sold: { asset: string; quantity: number; price: number; total: number };
      realized_pl: number;
    }>('/api/v1/child/investments/sell', {
      method: 'POST', body: JSON.stringify({ asset_id, quantity }),
    });
  },
};
